import "./styles.css";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Image,
  notification,
  Row,
  Space,
  Tag,
  theme,
  Tooltip,
  Typography,
} from "antd";
import { RangePickerProps } from "antd/lib/date-picker";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import {
  CaretRightOutlined,
  PaperClipOutlined,
  SaveTwoTone,
} from "@ant-design/icons";

import UserRequestCardProps from "../../../../propInterfaces/userRequestCardProps";
import {
  playManagerTrip,
  updateDescription,
  updateSheduleDate,
} from "../../../../services/userRequestApi";
import { CardItem } from "../../../../shared/components/CardImageText";
import {
  Calendar,
  CarIcon,
  CompanyIcon,
  DestinationIcon,
  DoorIcon,
  HashtagIcon,
  HotelIcon,
  KmIcon,
  ManagerIcon,
  MobileIcon,
  NoteIcon,
  OriginIcon,
  PlaneIcon,
  ProviderIcon,
  UserIcon,
} from "../../../../shared/constants/Images";
import {
  AntDesignWhite,
  IconDarkMode,
} from "../../../../shared/constants/Resources";
import { StatusTranslations } from "../../../../shared/constants/Status";
import { TRIP_STATUS } from "../../../../shared/constants/TripStatus";
import { generateClipboardText } from "../../../../utils/helpers/generateClipboardText";
import { sortDestinations } from "../../../../utils/helpers/sortDestinations";
import CreateEmptyTrip from "../../../emptyTrip/form/createEmptyTrip";
import EmptyTripDetails from "../../../emptyTrip/list/emptyTripDatails";
import { getBackgroundByStatus } from "../utils";

const { Paragraph, Text } = Typography;
const DEFAULT_DESCRIPTION = "No hay notas";

const disabledDate: RangePickerProps["disabledDate"] = (current) => {
  return current < dayjs().endOf("day").subtract(1, "day");
};

export const UserRequestCard = ({
  userRequest,
  isChecked,
  onSelect,
  setRefreshData,
}: UserRequestCardProps) => {
  const {
    token: { colorText },
  } = theme.useToken();
  const isTextDarkMode = colorText === AntDesignWhite;
  const [loading, setLoading] = useState(false);
  const [editableDescription, setEditableDescription] = useState(
    userRequest.description || DEFAULT_DESCRIPTION,
  );
  const [scheduleDate, setScheduleDate] = useState(
    dayjs(userRequest.scheduleAt, "DD-MM-YYYY HH:mm"),
  );
  const playableStatus = [
    TRIP_STATUS.Searching,
    TRIP_STATUS.Created,
    TRIP_STATUS.Scheduled,
    TRIP_STATUS.Started,
    TRIP_STATUS.Accepted,
  ];

  const distinctPassengers = Array.from(
    new Map(
      userRequest?.passengersData?.map((item) => [item.name, item]),
    ).values(),
  );

  const passengerPhone =
    userRequest.passengersData?.[0]?.phone ?? userRequest?.user?.mobile;

  const userRequestDtoStatus = useMemo(() => {
    return (
      StatusTranslations.find(
        (statusTranslation) => statusTranslation.name === userRequest.status,
      )?.translation ?? ""
    );
  }, [userRequest.status]);

  const backgroundColor = useMemo(() => {
    return getBackgroundByStatus(userRequestDtoStatus);
  }, [userRequestDtoStatus]);

  const isLote = userRequest?.passengersData?.length! > 1;

  const saveDescription = async () => {
    if (!userRequest.id) {
      return;
    }

    try {
      await updateDescription(userRequest.id, editableDescription);
      notification.success({
        message: "Descripción actualizada correctamente!",
        placement: "bottom",
        key: "global",
      });
      setRefreshData && setRefreshData(true);
    } catch (error) {
      notification.error({
        message: "Ha ocurrido un error",
        description:
          "Ha ocurrido un error al actualizar la descripción del viaje.",
        placement: "bottom",
        key: "global",
      });
    }
  };

  const onPlayManagerTrip = async () => {
    setLoading(true);
    try {
      await playManagerTrip(userRequest.bookingId!);
    } catch (e) {
      notification.error({
        message: "Error al reproducir el viaje",
        placement: "bottom",
      });
    } finally {
      setRefreshData && setRefreshData(true);
      setLoading(false);
    }
  };

  const saveScheduleDate = async () => {
    if (!userRequest.id) {
      return;
    }

    try {
      await updateSheduleDate(
        userRequest.id,
        scheduleDate.format("YYYY-MM-DD HH:mm:ss"),
      );
      notification.success({
        message: "Fecha de agendamiento actualizada correctamente!",
        placement: "bottom",
        key: "global",
      });
      setRefreshData && setRefreshData(true);
    } catch (error) {
      notification.error({
        message: "Ha ocurrido un error",
        description:
          "Ha ocurrido un error al actualizar la fecha de agendamiento del viaje.",
        placement: "bottom",
        key: "global",
      });
    }
  };

  return (
    <Card
      styles={{
        body: {
          padding: 0,
          background:
            userRequest.userTripNumber! <= 3
              ? isTextDarkMode
                ? "linear-gradient(45deg, #a28023 5%, #b19034 10%, #c0a044 30%, #cfb054 50%, #c0a044 70%, #b19034 80%, #a28023 95%)"
                : "linear-gradient(45deg, #d4af37 5%, #debc5b 10%, #e7c97c 30%, #efd69d 50%, #e7c97c 70%, #debc5b 80%, #d4af37 95%)"
              : isChecked
                ? isTextDarkMode
                  ? "rgb(99 99 99)"
                  : "#e5e5e5"
                : "",
        },
      }}
      hoverable
      onClick={(e) => onSelect(e, userRequest, !isChecked)}
    >
      <Row>
        <Col span={24} style={{ background: backgroundColor }}>
          <Row justify="center">
            <Text
              style={{
                color: AntDesignWhite,
              }}
              strong
            >
              {userRequestDtoStatus}
            </Text>
          </Row>
        </Col>
      </Row>
      <Row gutter={[0, 24]} style={{ padding: "20px 18px" }}>
        <Col span={22}>
          <Col>
            <CardItem
              isTextDarkMode={isTextDarkMode}
              src={HashtagIcon}
              text={userRequest.bookingId!}
              strong
            />
          </Col>
        </Col>
        <Col span={2}>
          <Space className="booking-id">
            <Tooltip title="Copiar al porta papeles">
              <Button
                shape="circle"
                icon={<PaperClipOutlined twoToneColor="#3b3c3c" />}
                onClick={async () =>
                  await generateClipboardText(userRequest, distinctPassengers)
                }
              />
            </Tooltip>
          </Space>
        </Col>
        {isLote && (
          <Col span={24}>
            <Row align="middle" gutter={[24, 24]}>
              <Col span={12}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={UserIcon}
                  text={`${userRequest?.user?.firstName} ${userRequest?.user?.lastName}`}
                />
              </Col>
              <Col span={12}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={MobileIcon}
                  text={userRequest?.user?.mobile!}
                />
              </Col>
              {distinctPassengers.map((passenger) => (
                <Col span={12}>
                  <CardItem
                    isTextDarkMode={isTextDarkMode}
                    src={UserIcon}
                    text={passenger.name}
                  />
                </Col>
              ))}
              <Col span={12}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={KmIcon}
                  text={String(userRequest?.distance)}
                />
              </Col>
            </Row>
          </Col>
        )}
        {!isLote && (
          <Col span={24}>
            <Row align="middle" justify="center">
              <Col span={12}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={UserIcon}
                  strong
                  text={
                    !!userRequest.passengersData?.length
                      ? userRequest.passengersData[0].name
                      : `${userRequest?.user?.firstName} ${userRequest?.user?.lastName}`
                  }
                />
              </Col>
              <Col span={12}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={MobileIcon}
                  strong
                  text={passengerPhone!}
                />
              </Col>
            </Row>
          </Col>
        )}
        <Col span={24}>
          <Row align="middle" justify="center">
            <Col span={12}>
              <Space>
                <Image
                  preview={false}
                  src={CompanyIcon}
                  alt=""
                  style={{
                    filter: isTextDarkMode ? IconDarkMode : "",
                  }}
                  width={20}
                />
                <Text>
                  Empresa: <b>{userRequest?.user?.admin?.name}</b>
                </Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <Image
                  preview={false}
                  src={NoteIcon}
                  alt="Nota"
                  style={{
                    filter: isTextDarkMode ? IconDarkMode : "",
                    minWidth: "22.5px",
                  }}
                  width={20}
                />
                <Paragraph
                  editable={
                    userRequest.status === TRIP_STATUS.Scheduled && {
                      onChange: setEditableDescription,
                    }
                  }
                  style={{ marginBottom: 0 }}
                >
                  {editableDescription}
                </Paragraph>
                {userRequest.status === TRIP_STATUS.Scheduled && (
                  <Tooltip title="Guardar cambios">
                    <Button
                      size="small"
                      disabled={
                        editableDescription === DEFAULT_DESCRIPTION ||
                        editableDescription === userRequest.description
                      }
                      type="text"
                      icon={<SaveTwoTone twoToneColor="#52c41a" />}
                      onClick={saveDescription}
                    />
                  </Tooltip>
                )}
              </Space>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row align="middle" justify="center">
            <Col span={12}>
              <CardItem
                isTextDarkMode={isTextDarkMode}
                src={OriginIcon}
                text={userRequest?.originAddress!}
                strong
              />
            </Col>
            <Col span={12}>
              <CardItem
                isTextDarkMode={isTextDarkMode}
                src={DestinationIcon}
                text={
                  userRequest.destinations?.length
                    ? sortDestinations(userRequest.destinations)[
                        userRequest.destinations?.length - 1
                      ].address!
                    : userRequest?.destinationAddress!
                }
                strong
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row align="middle">
            <Col span={24}>
              <CardItem
                isTextDarkMode={isTextDarkMode}
                src={CarIcon}
                text={`${userRequest?.serviceType?.name}`}
                strong
                isUrgent={userRequest?.serviceType?.name?.includes("Urgente")}
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row align="middle">
            <Col span={12}>
              <CardItem
                isTextDarkMode={isTextDarkMode}
                src={ProviderIcon}
                text={
                  userRequest.provider
                    ? `${userRequest?.provider?.firstName} ${userRequest?.provider?.lastName}`
                    : "No hay un conductor asignado"
                }
                strong
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row align="middle">
            <Col span={12}>
              <Space>
                <Image
                  preview={false}
                  src={Calendar}
                  alt=""
                  style={{
                    filter: isTextDarkMode ? IconDarkMode : "",
                  }}
                  width={20}
                />

                {userRequest.status === TRIP_STATUS.Scheduled && (
                  <>
                    <DatePicker
                      className="calendar"
                      disabledDate={disabledDate}
                      showTime
                      format="YYYY-MM-DD HH:mm"
                      placeholder="Seleccionar fecha"
                      allowClear={false}
                      value={scheduleDate}
                      onChange={(value) => setScheduleDate(value)}
                    />
                    <Tooltip title="Guardar cambios">
                      <Button
                        disabled={scheduleDate.isSame(
                          dayjs(userRequest.scheduleAt),
                        )}
                        type="text"
                        icon={<SaveTwoTone twoToneColor="#52c41a" />}
                        onClick={saveScheduleDate}
                      />
                    </Tooltip>
                  </>
                )}

                {userRequest.status !== TRIP_STATUS.Scheduled && (
                  <Text>
                    {userRequest.scheduleAt ??
                      dayjs(userRequest.createdAt).format("YYYY-MM-DD HH:mm")}
                  </Text>
                )}
              </Space>
            </Col>
          </Row>
        </Col>
        {(userRequest.departmentCode || userRequest.flightNumber) && (
          <Col span={24}>
            <Row align="middle">
              {userRequest.departmentCode && (
                <Col span={12}>
                  <CardItem
                    isTextDarkMode={isTextDarkMode}
                    src={DoorIcon}
                    text={`Departamento: ${userRequest.departmentCode}`}
                    strong
                  />
                </Col>
              )}
              {userRequest.flightNumber && (
                <Col span={12}>
                  <CardItem
                    isTextDarkMode={isTextDarkMode}
                    src={PlaneIcon}
                    text={`Numero de Vuelo: ${userRequest.flightNumber}`}
                    strong
                  />
                </Col>
              )}
            </Row>
          </Col>
        )}
        {userRequest.reason && (
          <Col span={24}>
            <Row align="middle">
              <Col span={24}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={NoteIcon}
                  text={`Motivo: ${userRequest.reason}`}
                  strong
                />
              </Col>
            </Row>
          </Col>
        )}
        {userRequest.responsible && (
          <Col span={24}>
            <CardItem
              isTextDarkMode={isTextDarkMode}
              src={ManagerIcon}
              text={`Responsable: ${userRequest.responsible}`}
              strong
            />
          </Col>
        )}
        {userRequest.qrFee && parseFloat(userRequest.qrFee.toString()) > 0 && (
          <Col span={24}>
            <Row align="middle">
              <Col span={24}>
                <Space>
                  <Tag color="green">
                    Partner{" "}
                    <Image
                      src={HotelIcon}
                      alt="hotel"
                      style={{
                        filter: isTextDarkMode ? IconDarkMode : "",
                      }}
                      width={20}
                      preview={false}
                    />
                  </Tag>
                </Space>
              </Col>
            </Row>
          </Col>
        )}
        {userRequest?.country?.name !== "Uruguay" && (
          <Col span={24}>
            <Row align="middle">
              <Col span={24}>
                <Tag color="magenta">Internacional</Tag>
                <Text>País: {userRequest.country!.name}</Text>
              </Col>
            </Row>
          </Col>
        )}
        <Col span={24}>
          <Row align="middle">
            <Col span={12}>
              {userRequest.emptyTrip ? (
                <EmptyTripDetails emptyTrip={userRequest.emptyTrip} />
              ) : (
                <CreateEmptyTrip userRequest={userRequest} />
              )}
            </Col>
            {playableStatus.some((x) => x === userRequest.status) && (
              <Col span={12}>
                <Button
                  type="primary"
                  icon={<CaretRightOutlined />}
                  onClick={() => onPlayManagerTrip()}
                  disabled={userRequest?.isTripPlaying ?? false}
                  loading={loading}
                  color="green"
                  variant="solid"
                >
                  Reproducir viaje
                </Button>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

export default UserRequestCard;
